/**
 * ScheduleReportForm Component
 *
 * Form for scheduling insight reports with granular scheduling control.
 * Displays with a gradient background overlay matching the design system.
 */

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { X } from 'lucide-react';
import { Input, Label, toast } from '@strivelabs/ui-core';
import { useCreateInsight } from '@/hooks/useCreateInsight';
import { ScheduleSelector } from './ScheduleSelector';
import {
  SCHEDULE_OPTIONS,
  DAYS_OPTIONS,
  DEFAULT_SCHEDULE_TIME,
  generateCronExpression,
} from '@/utils/schedule';

// Form validation schema
const formSchema = z.object({
  displayName: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export interface ScheduleReportFormProps {
  /** Workflow execution ID for the insight */
  workflowExecutionId: string;
  /** Insight preview blocks from behaviour action */
  blocks: unknown[];
  /** Default title from the behaviour action (user can override) */
  title?: string;
  /** Callback fired when the overlay is closed */
  onClose: () => void;
  /** Callback fired when the form is successfully submitted */
  onSuccess: () => void;
}

export const ScheduleReportForm: React.FC<ScheduleReportFormProps> = ({
  workflowExecutionId,
  blocks,
  title,
  onClose,
  onSuccess,
}) => {
  // Schedule state management
  const [schedule, setSchedule] = useState<string | null>('weekly');
  const [day, setDay] = useState<string | null>('1'); // Default to Monday (index 1)
  const [time, setTime] = useState<string>(DEFAULT_SCHEDULE_TIME);

  // Initialize form with default values
  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      displayName: title ?? '',
    },
  });

  // Create insight mutation
  const createMutation = useCreateInsight({
    onSuccess: () => {
      // Call parent success handler
      onSuccess();
    },
    onError: (error) => {
      // Show error toast
      toast({
        title: 'Failed to schedule report',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    },
  });

  // Get browser's local timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const isScheduleValid =
    schedule !== null && (schedule === 'daily' || day !== null) && time !== '';

  // Form submission handler
  const onSubmit = (data: FormData) => {
    // Generate cron expression from schedule settings
    const cron = generateCronExpression({ schedule, day, time });

    if (!cron) {
      toast({
        title: 'Invalid schedule',
        description: 'Please select a valid schedule configuration',
        variant: 'destructive',
      });
      return;
    }

    // Create the insight payload with default 30-day data range
    createMutation.mutate({
      insight: {
        source: 'CHAT',
        workflow_execution_id: workflowExecutionId,
        display_name: data.displayName?.trim() || 'Scheduled Report',
        blocks,
        schedule: {
          cron,
          timezone,
        },
        data_range: {
          type: 'relative',
          unit: 'DAYS',
          value: 30,
        },
      },
    });
  };

  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-black/50 backdrop-blur-[2px]">
      {/* Close Button - top right corner */}
      <button
        type="button"
        onClick={onClose}
        className="absolute right-6 top-6 z-20 rounded-full p-2 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        aria-label="Close"
      >
        <X size={24} />
      </button>

      {/* Gradient Overlay - positioned at top, allows content behind to show through */}
      <div
        className="absolute left-1/2 top-0 h-[1744px] w-[1601px] -translate-x-1/2 opacity-90"
        style={{
          background: 'var(--gradient-overlay)',
          filter: 'blur(137.65px)',
          borderRadius: '701px',
        }}
      />

      {/* Form - centered with Flexbox, responsive width */}
      <div
        className="relative z-10 w-full max-w-[520px] px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col items-center space-y-6">
          {/* Title */}
          <h1 className="font-fraunces-display text-center text-[40px] font-normal leading-[44px] tracking-[-0.8px] text-white">
            Schedule report
          </h1>

          {/* Subtitle */}
          <p className="max-w-[267px] text-center text-sm leading-6 tracking-[-0.28px] text-white/60">
            You will receive this report automatically based on this schedule
          </p>

          {/* Form Fields - centered with consistent spacing */}
          <div className="flex w-full max-w-[286px] flex-col gap-4 pt-4">
            {/* Display Name Input */}
            <div className="w-full">
              <Label htmlFor="displayName" className="text-sm text-white/80">
                Report Name
              </Label>
              <Input
                id="displayName"
                {...register('displayName')}
                placeholder="Report name (optional)"
                disabled={createMutation.isPending}
                className="mt-1 h-auto w-full rounded-md border-[0.5px] border-white/10 bg-white/[0.08] px-3 pb-[9px] pt-[8px] text-sm text-white placeholder:text-white/40 backdrop-blur-sm hover:bg-white/10 focus-visible:ring-white/20"
              />
            </div>

            {/* Schedule Selector */}
            <ScheduleSelector
              schedule={schedule}
              day={day}
              time={time}
              scheduleOptions={SCHEDULE_OPTIONS}
              dayOptions={DAYS_OPTIONS}
              onScheduleChange={setSchedule}
              onDayChange={setDay}
              onTimeChange={setTime}
              disabled={createMutation.isPending}
            />

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!isValid || !isScheduleValid || createMutation.isPending}
              className="w-full rounded-md bg-white px-3 pb-3 pt-[11px] text-center text-sm font-medium text-[#181818] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createMutation.isPending ? 'Scheduling...' : 'Schedule report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

